import { useCallback, useRef } from 'react';

/**
 * Remembers where a scroll container was left, across unmounts.
 *
 * Switching to My Schedule unmounts both Explore columns. Without this, a user
 * who had scrolled hundreds of events deep and glanced at their schedule came
 * back to the top of the list — losing a position that takes a long time to
 * reach and cannot be searched back to.
 *
 * Restoring happens in a **callback ref**, not an effect. The hook is called
 * from a component that outlives the pane it tracks, so a mount effect fires
 * once, long before the node the user actually scrolled ever exists. The
 * callback ref runs every time a node is attached, which is exactly when the
 * position needs putting back.
 *
 * The state lives at module level for the same reason — the component that
 * would otherwise hold it is not the one being unmounted. It is per-session by
 * design: a fresh page load should start at the top.
 */

const positions = new Map<string, number>();

/** Live panes, so "start over" can move them and not merely forget them. */
const nodes = new Map<string, HTMLDivElement>();

/** In-flight restore loops, so a later intent can cancel an earlier one. */
const loops = new Map<string, number>();

function stopRestore(key: string) {
  const id = loops.get(key);
  if (id !== undefined) {
    cancelAnimationFrame(id);
    loops.delete(key);
  }
}

export function useScrollMemory(key: string, enabled = true) {
  const node = useRef<HTMLDivElement | null>(null);

  const attach = useCallback(
    (el: HTMLDivElement | null) => {
      node.current = el;
      if (!enabled) return;

      if (!el) {
        nodes.delete(key);
        stopRestore(key);
        return;
      }
      nodes.set(key, el);

      const saved = positions.get(key);
      if (!saved) return;

      el.scrollTop = saved;

      /**
       * The events list renders incrementally, so at restore time its content
       * is usually still shorter than the saved offset and the browser clamps
       * what we just set. Re-apply while the pages land.
       *
       * The loop stops the moment the position holds, and never merely runs out
       * its budget: content arriving *below* the viewport cannot shift the
       * offset, so once it has taken there is nothing left to correct — and a
       * loop still ticking is one that would undo a deliberate scroll to the
       * top a moment later.
       */
      stopRestore(key);
      let frames = 0;
      const reapply = () => {
        const current = node.current;
        if (!current || Math.abs(current.scrollTop - saved) <= 1) {
          loops.delete(key);
          return;
        }
        if (current.scrollHeight > saved) current.scrollTop = saved;
        frames += 1;
        // Give up rather than spin if the content is simply shorter now.
        if (frames < 60) loops.set(key, requestAnimationFrame(reapply));
        else loops.delete(key);
      };
      loops.set(key, requestAnimationFrame(reapply));
    },
    [key, enabled],
  );

  /**
   * Saved on every scroll rather than on unmount: by the time an unmount
   * cleanup runs the node is detached and reports a scrollTop of 0, which would
   * overwrite a good position with nothing.
   */
  const onScroll = useCallback(() => {
    if (!enabled) return;
    const el = node.current;
    if (el) positions.set(key, el.scrollTop);
  }, [key, enabled]);

  /** Back to the top, and forget — for when the content has been replaced. */
  const toTop = useCallback(() => clearScrollMemory(key), [key]);

  // Exposed because the pane is the hook's to hold but not only the hook's to
  // read: list anchoring measures the same node.
  return { ref: attach, onScroll, toTop, node };
}

/**
 * Forget a remembered position and send that pane back to the top. With no key,
 * does it for every pane — which is what the wordmark's "start over" means.
 */
export function clearScrollMemory(key?: string) {
  const keys = key ? [key] : [...new Set([...nodes.keys(), ...positions.keys()])];
  for (const k of keys) {
    positions.delete(k);
    // Before moving anything: a restore still in flight would put it straight
    // back, which is exactly what "start over" is trying to undo.
    stopRestore(k);
    const node = nodes.get(k);
    if (node) node.scrollTop = 0;
  }
}
